/**
 * The audio cue bank (Phase 8): which raw files play for which cue, on which bus, how many at
 * once, with what pitch and caption. The pipeline builds one processed file per raw file named
 * here, measures it and records the play-time gain that brings it to its bus's loudness target
 * (docs/ADR/0004-web-audio.md); the client reads the same bank to choose and place voices.
 */
import { z } from 'zod';
export const AUDIO_BUSES = ['sfx', 'music', 'ui'];
/** Integrated loudness each bus normalises to (EBU R128 measurement, BS.1770-4). */
export const AUDIO_TARGET_LUFS = {
    sfx: -16,
    music: -18,
    ui: -20,
};
/** No cue may pass this true peak after its gain. */
export const AUDIO_TRUE_PEAK_CEILING_DBTP = -1;
/** A cue must land this close to its target after the gain, else the build fails. */
export const AUDIO_LOUDNESS_TOLERANCE_LU = 1.5;
const cueId = z
    .string()
    .regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/, 'cue ids are dotted kebab-case');
const rawPath = z
    .string()
    .regex(/^[A-Za-z0-9_][A-Za-z0-9_ ./-]*\.(ogg|mp3|wav)$/, 'a path under assets/raw')
    .refine((p) => !p.split('/').includes('..'), 'no ".." segments');
export const AudioCueSchema = z
    .object({
    id: cueId,
    /** Files relative to assets/raw; one is chosen per play, never the same one twice running. */
    files: z.array(rawPath).min(1),
    bus: z.enum(AUDIO_BUSES),
    /** Placed at the event's world position (a panner); otherwise heard as-is. */
    positional: z.boolean(),
    /** Voices of this cue that may sound at once; a new one steals the oldest when full. */
    maxInstances: z.number().int().min(1).max(16).default(4),
    /** Higher wins a voice when the pool is full. */
    priority: z.number().int().min(0).max(10).default(5),
    /** Playback rate multiplier and the random spread around it. */
    pitch: z.number().min(0.25).max(4).default(1),
    pitchVariance: z.number().min(0).max(1).default(0),
    loop: z.boolean().default(false),
    /** Seconds before the same cue may sound again (0 = no limit). */
    retrigger: z.number().min(0).max(10).default(0),
    /** Mix trim after normalisation, dB. */
    mix: z.number().min(-30).max(12).default(0),
    /** Loudness target override, LUFS (the bus target otherwise). */
    target: z.number().min(-40).max(-6).optional(),
    /** Shown as a caption when the accessibility setting is on. */
    caption: z.string().min(1).max(40).optional(),
})
    .strict();
const WeaponCuesSchema = z.object({ shot: cueId }).strict();
export const AudioBankSchema = z
    .object({
    version: z.literal(1),
    cues: z.array(AudioCueSchema).min(1),
    /** The shot cue per weapon kind when the weapon has no entry of its own. */
    kinds: z.object({ hitscan: cueId, projectile: cueId, beam: cueId, melee: cueId }).strict(),
    /** Per-weapon overrides (keys are weapon ids, checked against the weapon data on load). */
    weapons: z.record(z.string(), WeaponCuesSchema),
    music: z.object({ menu: cueId, match: cueId, suddenDeath: cueId }).strict(),
})
    .strict()
    .superRefine((bank, ctx) => {
    const ids = new Set();
    bank.cues.forEach((cue, i) => {
        if (ids.has(cue.id)) {
            ctx.addIssue({
                code: 'custom',
                message: `duplicate cue id "${cue.id}"`,
                path: ['cues', i],
            });
        }
        ids.add(cue.id);
    });
    const refs = [
        ['kinds.hitscan', bank.kinds.hitscan],
        ['kinds.projectile', bank.kinds.projectile],
        ['kinds.beam', bank.kinds.beam],
        ['kinds.melee', bank.kinds.melee],
        ['music.menu', bank.music.menu],
        ['music.match', bank.music.match],
        ['music.suddenDeath', bank.music.suddenDeath],
        ...Object.entries(bank.weapons).map(([w, c]) => [
            `weapons.${w}.shot`,
            c.shot,
        ]),
    ];
    for (const [where, id] of refs) {
        if (id !== undefined && !ids.has(id)) {
            ctx.addIssue({ code: 'custom', message: `${where} names unknown cue "${id}"` });
        }
    }
    for (const id of [bank.music.menu, bank.music.match, bank.music.suddenDeath]) {
        const cue = bank.cues.find((c) => c.id === id);
        if (cue && (cue.bus !== 'music' || !cue.loop || cue.positional)) {
            ctx.addIssue({
                code: 'custom',
                message: `music cue "${id}" must be a looping, non-positional cue on the music bus`,
            });
        }
    }
});
/** The loudness a cue's files are normalised to. */
export function cueTargetLufs(cue) {
    return cue.target ?? AUDIO_TARGET_LUFS[cue.bus];
}
/**
 * Where the pipeline writes a raw audio file: `audio/` plus the path with its vendor folder
 * dropped, `Audio/` folders removed and the rest joined with dashes, so files that share a name
 * across packs (both Kenney packs have an `impactMetal_000.ogg`) stay apart.
 */
export function processedAudioPath(raw) {
    const segments = raw
        .split('/')
        .slice(1)
        .filter((s) => s !== 'Audio');
    return `audio/${segments.join('-').replace(/\s+/g, '-')}`;
}
/** Every distinct raw file the bank names, in first-seen order. */
export function bankFiles(bank) {
    const seen = new Set();
    for (const cue of bank.cues)
        for (const f of cue.files)
            seen.add(f);
    return [...seen];
}
/** What the pipeline measured per processed file; the client applies the gain and the loop trims. */
export const AudioSidecarSchema = z
    .object({
    version: z.literal(1),
    files: z.record(z.string(), z
        .object({
        /** Play-time gain that brings the file to its target, dB. */
        gainDb: z.number(),
        seconds: z.number().positive(),
        /** Loop points inside the file (leading and trailing near-silence trimmed). */
        loopStart: z.number().nonnegative(),
        loopEnd: z.number().positive(),
    })
        .strict()),
})
    .strict();
//# sourceMappingURL=audio-schema.js.map